import type { NavigationSection, NavigationItemLink } from "./navigation.data";
import Link from "next/link";
import Image from "next/image";
import { useState } from "react";
import clsx from "clsx";
import MegaMenuFooter from "./MegaMenuFooter";
import ChevronRightIcon from "../icons/ChevronRightIcon";

// Overview roles and category icons are explicit data, independent of copy.
const DEFAULT_PROMO_IMAGE =
  "/images/navigation/default-promo.webp";

function CategoryIcon({ icon }: { icon?: NavigationSection["icon"] }) {
  switch (icon) {
    case "darkplane":
      return (
        <svg width="29" height="27" viewBox="0 0 29 27" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14.498 0C14.6907 7.44571e-05 14.8685 0.105875 14.9599 0.275391L28.9336 26.2275C29.0391 26.4238 
          29.0097 26.6657 28.8594 26.8301C28.709 26.9945 28.4704 27.0457 28.2656 26.958L18.9707 22.9736C18.961 22.97 18.9518 
          22.9652 18.9424 22.9609L14.498 21.0566L10.0527 22.9609C10.0432 22.9651 10.0341 22.97 10.0244 22.9736L0.730453 
          26.958C0.52577 27.0455 0.288044 26.9942 0.137679 26.8301C-0.0127433 26.6656 -0.0431379 26.4238 0.0624838 
          26.2275L14.0371 0.275391L14.0752 0.214844C14.1729 0.0811448 14.3296 0.000122836 14.498 0ZM1.69725 25.4023L4.77342 
          24.084L11.4287 7.3291L1.69725 25.4023ZM24.2217 24.084L27.2988 25.4023L17.5664 7.3291L24.2217 24.084ZM6.1328 23.501L9.3867 
          22.1064L12.6377 7.12598L6.1328 23.501ZM19.6094 22.1074L22.8633 23.501L16.3594 7.12988L19.6094 22.1074ZM15.0224 20.1416L18.4267 
          21.6006L15.0224 5.90918V20.1416ZM10.5693 21.5996L13.9736 20.1416V5.91406L10.5693 21.5996Z" fill="currentColor"/>
        </svg>


      );
    case "observability":
      return (
        <svg width="25" height="23" viewBox="0 0 25 23" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fillRule="evenodd" clipRule="evenodd" d="M8.96958 0C9.45218 0 9.88063 0.308811 10.0332 0.766643L15.6968 17.7572L17.9967
          10.8574C18.1493 10.3996 18.5778 10.0908 19.0604 10.0908H23.5451C24.1644 10.0908 24.6663 10.5928 24.6663 11.212C24.6663 11.8312 
          24.1644 12.3332 23.5451 12.3332H19.8685L16.7604 21.6573C16.6078 22.1151 16.1794 22.4239 15.6968 22.4239C15.2142 22.4239 14.7857 
          22.1151 14.6331 21.6573L8.96958 4.66673L6.66965 11.5665C6.51704 12.0244 6.08858 12.3332 5.60599 12.3332H1.1212C0.501977 12.3332 
          0 11.8312 0 11.212C0 10.5928 0.501977 10.0908 1.1212 10.0908H4.79787L7.90592 0.766643C8.05853 0.308811 8.48698 0 8.96958 0Z" 
          fill="currentColor" />
        </svg>

      );
    case "agent0":
      return (
        <svg width="25" height="22" viewBox="0 0 25 22" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.8115 15.1426C18.8504 15.2232 19.8245 15.3112 20.6172 15.3887C19.7645 19.0851 16.4562 21.8423 
          12.501 21.8428C8.55744 21.8428 5.25485 19.1025 4.38965 15.4229C5.1547 15.3464 6.13417 15.2553 7.19727 15.1699C7.90354 
          17.4267 10.0115 19.0643 12.501 19.0645C15.0003 19.0642 17.1138 17.4132 17.8115 15.1426ZM12.5 10.0371C12.7693 10.0371 13.0315 
          10.0689 13.2832 10.127C13.2253 10.3183 13.1934 10.5212 13.1934 10.7314C13.1934 11.8819 14.1269 12.8143 15.2773 12.8145C15.4872 
          12.8144 15.6898 12.7824 15.8809 12.7246C15.9392 12.9769 15.9717 13.2398 15.9717 13.5098C15.9714 15.427 14.4173 16.9812 12.5 
          16.9814C10.5827 16.9813 9.02763 15.4271 9.02734 13.5098C9.02734 11.5922 10.5825 10.0373 12.5 10.0371ZM12.5 6.56445C19.4024 
          6.56445 24.9982 7.80801 25 9.3418C25 10.192 23.2807 10.9493 20.5732 11.458C20.7257 12.0572 20.8152 12.6819 20.8291 13.3252C20.0565 
          13.2486 19.0847 13.1604 18.0371 13.0771C17.9717 12.2272 17.7142 11.4313 17.3096 10.7314H17.3125C16.3519 9.07099 14.5571 7.95318 12.501 7.95312C10.4447 7.95312 8.64921 
          9.07089 7.68848 10.7314H7.69141C7.28191 11.4391 7.0228 12.2447 6.96094 13.1055C5.88599 13.1938 4.90879 13.2854 4.16992 13.3604C4.18141 12.7047 4.27053 12.0681 4.42578 
          11.458C1.71884 10.9494 0 10.1919 0 9.3418C0.00182552 7.80802 5.59756 6.56446 12.5 6.56445ZM12.0635 0C15.7188 0 18.802 2.43989 19.7793 5.7793C17.7292 
          5.40021 15.2158 5.17578 12.5 5.17578C9.67027 5.17579 7.05994 5.41767 4.96484 5.82617C5.62118 2.50483 8.54957 0.000115391 12.0635 0Z" fill="currentColor" />
        </svg>

      );
    case "signalstore":
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
          <ellipse cx="12" cy="6" rx="8" ry="3" stroke="currentColor" strokeWidth="1.5" />
          <path d="M4 6v6c0 1.66 3.58 3 8 3s8-1.34 8-3V6" stroke="currentColor" strokeWidth="1.5" />
          <path d="M4 12v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      );
    default:
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
          <circle cx="12" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      );
  }
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-[#FCEA981F] text-yellow text-[9px] leading-none px-1.5 py-1 rounded-sm font-mono uppercase tracking-widest shrink-0">
      {children}
    </div>
  );
}

function ItemLink({ item, clickSubmenu }: { item: NavigationItemLink; clickSubmenu: (link: string, external?: boolean) => void }) {
  return (
    <Link
      href={item.link}
      target={item.external ? "_blank" : undefined}
      onClick={() => clickSubmenu(item.link, item.external)}
      className="group flex flex-col gap-1"
    >
      <span className="flex items-center gap-2">
        <span className="text-white text-sm font-medium leading-tight transition-colors group-hover:text-white/70">
          {item.title}
        </span>
        {item.badge && <Badge>{item.badge}</Badge>}
      </span>
      <span className="text-grey text-[12px] font-normal leading-snug">
        {item.subtitle}
      </span>
    </Link>
  );
}

const PROMO_CARD_CLASS =
  "flex h-full flex-col rounded-xl border border-[#616161]/50 overflow-hidden";

function PromoCardShell({
  promo,
  clickSubmenu,
  children,
}: {
  promo: { title: string; link?: string; external?: boolean };
  clickSubmenu: (link: string, external?: boolean) => void;
  children: React.ReactNode;
}) {
  if (!promo.link) {
    return <div className={PROMO_CARD_CLASS}>{children}</div>;
  }

  return (
    <Link
      href={promo.link}
      target={promo.external ? "_blank" : undefined}
      onClick={() => clickSubmenu(promo.link!, promo.external)}
      className={clsx(
        PROMO_CARD_CLASS,
        "group transition-shadow duration-200 ease-out hover:shadow-[0_0_0_3px_rgba(97,97,97,0.9)]"
      )}
    >
      {children}
    </Link>
  );
}

export default function ProductMegaMenu({
  clickSubmenu,
  sections,
  footerLabel,
  footerCta,
}: {
  clickSubmenu: (link: string, external?: boolean) => void;
  sections: NavigationSection[];
  footerLabel?: string;
  footerCta?: { title?: string; link?: string };
}) {
  const categories = sections ?? [];
  const [activeKey, setActiveKey] = useState<string | undefined>(categories[0]?.id);
  const active = categories.find((c) => c.id === activeKey) ?? categories[0];

  if (!active) return null;

  const allLinks = active.links ?? [];
  const items = allLinks.filter((link) => link.role !== "overview");
  const groups = active.groups ?? [];
  // The Overview link is hidden from the list above because the promo card is
  // the desktop route to a category's overview page. Use it as the card's
  // destination whenever no explicit promo link is authored, so filtering
  // Overview out never makes that page unreachable.
  const overviewLink = allLinks.find((link) => link.role === "overview");
  const promo = {
    title: active.promo?.title ?? active.title ?? "",
    subtitle: active.promo?.subtitle ?? active.description,
    image: active.promo?.image ? active.promo.image : DEFAULT_PROMO_IMAGE,
    link: active.promo?.link ?? overviewLink?.link,
    external: active.promo?.link ? undefined : overviewLink?.external,
  };

  return (
    <MegaMenuFooter clickSubmenu={clickSubmenu} footerLabel={footerLabel} footerCta={footerCta}>
      <div className="flex xl:h-full xl:flex-row flex-col gap-2">
        {/* Left rail: category selector */}
        <div className="flex flex-col gap-1 xl:w-[280px] shrink-0 xl:pr-3">
          {categories.map((category) => {
            const isActive = category.id === active.id;
            return (
              <button
                key={category.id}
                type="button"
                onMouseEnter={() => setActiveKey(category.id)}
                onFocus={() => setActiveKey(category.id)}
                className={clsx(
                  "flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors duration-150",
                  isActive ? "bg-grey-850" : "hover:bg-grey-850/50"
                )}
              >
                <span className={clsx("shrink-0 transition-colors", isActive ? "text-white" : "text-grey-550")}>
                  <CategoryIcon icon={category.icon} />
                </span>
                <span className="flex flex-col gap-0.5">
                  <span className="text-white text-[16px] font-medium leading-tight">{category.title}</span>
                  {category.description && (
                    <span className="text-grey text-[12px] font-normal leading-snug">{category.description}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>

        {/* Middle: selected category's links, flat links first, then any groups */}
        <div className="flex flex-col gap-6 xl:w-[440px] shrink-0 xl:border-l xl:border-grey-850 xl:pl-8 xl:pt-1">
          {items.length > 0 && (
            <div className="grid grid-cols-2 gap-x-10 gap-y-6 content-start">
              {items.map((item) => (
                <ItemLink key={item.id} item={item} clickSubmenu={clickSubmenu} />
              ))}
            </div>
          )}
          {groups.map((group) => (
            <div key={group.id} className="flex flex-col gap-3">
              {group.title && (
                <span className="text-grey-600 text-[11px] uppercase font-semibold tracking-wider leading-none">
                  {group.title}
                </span>
              )}
              <div className="grid grid-cols-2 gap-x-10 gap-y-6 content-start">
                {group.links?.map((item) => (
                  <ItemLink key={item.id} item={item} clickSubmenu={clickSubmenu} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Right: promo card tied to the selected category. Rendered as a plain
            container when neither a promo link nor an Overview link exists, so
            it never becomes a link to nowhere. */}
        <div className="xl:w-[300px] shrink-0">
          <PromoCardShell promo={promo} clickSubmenu={clickSubmenu}>
            <div className="flex flex-col gap-0.5 px-5 pt-5">
              <span className="text-white text-[16px] font-normal leading-tight">{promo.title}</span>
              {promo.subtitle && (
                <span className="text-grey text-[12px] font-normal leading-snug">{promo.subtitle}</span>
              )}
            </div>
            <div className="relative mt-4 flex-1 min-h-[150px] overflow-hidden">
              <div className="absolute inset-0 ml-5">
                <Image
                  key={active.id}
                  src={promo.image}
                  alt={promo.title}
                  fill
                  sizes="300px"
                  className="object-cover object-left-bottom"
                />
              </div>
              {promo.link && (
                <>
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-linear-to-t from-black via-black/70 to-transparent" />
                  <span className="absolute bottom-5 left-5 inline-flex items-center gap-0.5 rounded bg-white px-2 py-1 text-[12px] font-medium text-grey-950 transition-colors duration-200 group-hover:bg-grey-200 [&_svg]:h-3 [&_svg]:w-3 [&_svg]:transition-transform [&_svg]:duration-200 group-hover:[&_svg]:translate-x-0.5">
                    Explore {promo.title}
                    <ChevronRightIcon />
                  </span>
                </>
              )}
            </div>
          </PromoCardShell>
        </div>
      </div>
    </MegaMenuFooter>
  );
}
